import React, { useEffect, useState } from 'react';
import { FaBalanceScale, FaFileInvoiceDollar, FaPlus, FaSave, FaSync } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../services/api';
import {
    BALANCING_MODEL_LABELS,
    MONTH_NAMES,
    canIssueBalancing,
    describeApiError,
    describeIssueResult,
    monthKey,
    monthLabel,
    monthRows,
    normalizeAmount,
    previousMonth,
    validateAmount,
} from '../services/balancing';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount);

const Badge = ({ label, color }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${color}`}>{label}</span>
);

const RUN_BADGES = {
    pending: ['W toku', 'bg-yellow-200 text-yellow-800'],
    completed: ['Zakończone', 'bg-green-200 text-green-800'],
    failed: ['Błąd', 'bg-red-200 text-red-800'],
};

const runBadge = (run) => {
    const [label, color] = (run && RUN_BADGES[run.status]) || ['Brak', 'bg-gray-200 text-gray-700'];
    return <Badge label={label} color={color} />;
};

const balancingBadge = (row) => {
    if (row.balancingIssued) return <Badge label="Wystawione" color="bg-green-200 text-green-800" />;
    if (row.run?.status === 'completed') return <Badge label="Niewystawione" color="bg-gray-200 text-gray-700" />;
    return <span className="text-gray-400">—</span>;
};

const Balancing = () => {
    const [settings, setSettings] = useState(null);
    const [costs, setCosts] = useState([]);
    const [billingRuns, setBillingRuns] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Koszty miesięczne (model ZSE): poprawiane w wierszu miesiąca albo wpisywane dla nowego miesiąca
    const [costEdits, setCostEdits] = useState({});
    const [savingCost, setSavingCost] = useState(null);
    const [issuing, setIssuing] = useState(null);
    const [newCost, setNewCost] = useState(() => ({ ...previousMonth(new Date()), amount: '' }));

    useEffect(() => {
        const fetchAll = async () => {
            try {
                setLoading(true);
                setError(null);
                const [settingsResponse, costsResponse, runsResponse, invoicesResponse] = await Promise.all([
                    api.get('/settings/'),
                    api.get('/invoices/balancing-costs'),
                    api.get('/invoices/billing-runs'),
                    api.get('/invoices/'),
                ]);
                setSettings(settingsResponse.data);
                setCosts(costsResponse.data);
                setBillingRuns(runsResponse.data);
                setInvoices(invoicesResponse.data);
            } catch (error) {
                console.error('Failed to fetch balancing data', error);
                setError(describeApiError(error, 'Nie udało się pobrać danych bilansowania'));
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const clearMessages = () => {
        setError(null);
        setSuccessMessage('');
    };

    const active = Boolean(settings?.balancing_active);
    const fixedRate = settings?.balancing_model === 'fixed_rate';
    const costSplit = settings?.balancing_model === 'cost_split';

    // Bez stawki model stałej stawki nic nie fakturuje — stawkę wpisuje się w Ustawieniach
    const rateMissing = fixedRate && settings?.balancing_rate_per_mwh == null;

    const rows = settings ? monthRows({ costs, billingRuns, invoices }) : [];

    const costInputValue = (row) => costEdits[row.key] ?? row.cost ?? '';

    const costDirty = (row) => {
        if (costEdits[row.key] === undefined) return false;
        const text = normalizeAmount(costEdits[row.key]);
        if (row.cost == null) return text !== '';
        return text === '' || Number(text) !== Number(row.cost);
    };

    const saveCost = async (year, month, value) => {
        clearMessages();
        const amount = normalizeAmount(value);
        const validation = validateAmount(amount);
        if (validation) {
            setError(validation);
            return false;
        }

        const key = monthKey(year, month);
        try {
            setSavingCost(key);
            const response = await api.put(`/invoices/balancing-costs/${year}/${month}`, { amount });
            setCosts((previous) => [
                ...previous.filter((cost) => !(cost.year === year && cost.month === month)),
                response.data,
            ]);
            setCostEdits((previous) => {
                const next = { ...previous };
                delete next[key];
                return next;
            });
            setSuccessMessage(
                `Zapisano koszty bilansowania za ${monthLabel(year, month).toLowerCase()}: ${formatCurrency(response.data.amount)}`
            );
            return true;
        } catch (error) {
            console.error('Failed to save balancing cost', error);
            setError(describeApiError(error, 'Nie udało się zapisać kosztów bilansowania'));
            return false;
        } finally {
            setSavingCost(null);
        }
    };

    const handleAddCost = async (e) => {
        e.preventDefault();
        if (await saveCost(newCost.year, newCost.month, newCost.amount)) {
            setNewCost({ ...newCost, amount: '' });
        }
    };

    const refreshInvoices = async () => {
        try {
            const response = await api.get('/invoices/');
            setInvoices(response.data);
        } catch (error) {
            console.error('Failed to refresh invoices', error);
        }
    };

    const handleIssue = async (row) => {
        if (!window.confirm(`Wystawić faktury za bilansowanie za ${monthLabel(row.year, row.month)}?`)) return;
        clearMessages();
        try {
            setIssuing(row.key);
            const response = await api.post(`/invoices/generate-balancing?year=${row.year}&month=${row.month}`);
            const outcome = describeIssueResult(response.data, row.year, row.month);
            if (outcome.ok) {
                setSuccessMessage(outcome.message);
            } else {
                setError(outcome.message);
            }
            await refreshInvoices();
        } catch (error) {
            console.error('Failed to issue balancing invoices', error);
            setError(describeApiError(error, 'Nie udało się wystawić faktur za bilansowanie'));
        } finally {
            setIssuing(null);
        }
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

    const renderCostCell = (row) => {
        const locked = row.balancingIssued;
        const saving = savingCost === row.key;
        return (
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    inputMode="decimal"
                    value={costInputValue(row)}
                    onChange={(e) => setCostEdits({ ...costEdits, [row.key]: e.target.value })}
                    disabled={locked || saving}
                    title={locked ? 'Faktury za ten miesiąc są już wystawione — żeby zmienić koszty, usuń rozliczenie miesiąca' : undefined}
                    placeholder="np. 1780,41"
                    className="w-32 p-2 border border-gray-300 rounded-lg text-right disabled:bg-gray-100 disabled:text-gray-500"
                />
                <span className="text-sm text-gray-500">zł</span>
                {costDirty(row) && (
                    <button
                        type="button"
                        onClick={() => saveCost(row.year, row.month, costEdits[row.key])}
                        disabled={saving}
                        className="bg-softTeal text-white px-3 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center text-sm whitespace-nowrap"
                    >
                        <FaSave className="mr-1" />
                        {saving ? 'Zapis...' : 'Zapisz'}
                    </button>
                )}
            </div>
        );
    };

    const renderIssueButton = (row) => {
        if (!canIssueBalancing(row, settings)) return null;
        const busy = issuing === row.key;
        return (
            <button
                type="button"
                onClick={() => handleIssue(row)}
                disabled={busy}
                className="bg-softTeal text-white px-3 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center text-sm whitespace-nowrap"
            >
                {busy ? <FaSync className="mr-2 animate-spin" /> : <FaFileInvoiceDollar className="mr-2" />}
                {busy ? 'Wystawianie...' : 'Wystaw faktury'}
            </button>
        );
    };

    const renderMobileList = () => (
        <div className="md:hidden space-y-4">
            {rows.map((row) => (
                <div key={row.key} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                        <p className="font-bold text-lg">{monthLabel(row.year, row.month)}</p>
                        {runBadge(row.run)}
                    </div>
                    {costSplit && (
                        <div className="mb-3">
                            <span className="text-gray-600 text-sm">Koszty operacyjne:</span>
                            <div className="mt-1">{renderCostCell(row)}</div>
                        </div>
                    )}
                    <div className="flex justify-between items-center gap-2">
                        <div>
                            <span className="text-gray-600 text-sm">Faktury za bilansowanie: </span>
                            {balancingBadge(row)}
                        </div>
                        {renderIssueButton(row)}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderDesktopTable = () => (
        <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="border px-4 py-2 text-left">Miesiąc</th>
                        <th className="border px-4 py-2 text-center">Rozliczenie miesiąca</th>
                        {costSplit && <th className="border px-4 py-2 text-left">Koszty operacyjne</th>}
                        <th className="border px-4 py-2 text-center">Faktury za bilansowanie</th>
                        <th className="border px-4 py-2 text-center">Akcje</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className="hover:bg-gray-50">
                            <td className="border px-4 py-2 font-semibold">{monthLabel(row.year, row.month)}</td>
                            <td className="border px-4 py-2 text-center">{runBadge(row.run)}</td>
                            {costSplit && <td className="border px-4 py-2">{renderCostCell(row)}</td>}
                            <td className="border px-4 py-2 text-center">{balancingBadge(row)}</td>
                            <td className="border px-4 py-2 text-center">
                                {renderIssueButton(row) || <span className="text-gray-300">-</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen p-4">
                <main className="container mx-auto">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                        <p className="text-center py-4">Ładowanie...</p>
                    </div>
                </main>
            </div>
        );
    }

    // Bez ustawień (np. konto tylko do odczytu dostaje 403) nie ma czego pokazać
    if (!settings) {
        return (
            <div className="min-h-screen p-4">
                <main className="container mx-auto">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                        <h2 className="text-xl sm:text-2xl font-bold flex items-center mb-4">
                            <FaBalanceScale className="mr-2 text-softTeal" />
                            Bilansowanie
                        </h2>
                        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                {/* Stan bilansowania */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold flex items-center mb-2">
                        <FaBalanceScale className="mr-2 text-softTeal" />
                        Bilansowanie
                    </h2>
                    <p className="text-gray-600 text-sm mb-4">
                        Spółdzielnia co miesiąc wystawia członkom fakturę za bilansowanie energii — jeden wiersz na każde PPE, w MWh.
                        Włącznik i model bilansowania ustawia operator.
                    </p>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm items-center">
                        <div>
                            <span className="text-gray-600 mr-2">Stan:</span>
                            {active
                                ? <Badge label="Włączone" color="bg-green-200 text-green-800" />
                                : <Badge label="Wyłączone" color="bg-gray-200 text-gray-700" />}
                        </div>
                        <div>
                            <span className="text-gray-600 mr-2">Model:</span>
                            <span className="font-semibold">{BALANCING_MODEL_LABELS[settings.balancing_model] || settings.balancing_model}</span>
                        </div>
                    </div>
                    {!active && (
                        <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg text-sm">
                            Bilansowanie jest wyłączone — faktury za bilansowanie nie są wystawiane. Włącza je operator na stronie instancji.
                        </div>
                    )}
                    {active && rateMissing && (
                        <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg text-sm">
                            Brak stawki za bilansowanie — bez niej faktury za bilansowanie nie powstaną.{' '}
                            <Link to="/settings" className="underline">Wpisz ją w Ustawieniach</Link>.
                        </div>
                    )}
                    <p className="mt-4 text-gray-500 text-xs">
                        Stawkę za MWh i format numeru faktury za bilansowanie zmienisz w{' '}
                        <Link to="/settings" className="underline">Ustawieniach spółdzielni</Link>.
                    </p>
                </div>

                {/* Komunikaty */}
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                        {successMessage}
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Miesiące: koszty (model ZSE) i faktury za bilansowanie */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <h3 className="text-lg font-bold mb-1">
                        {costSplit ? 'Miesięczne koszty bilansowania' : 'Faktury za bilansowanie'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                        {costSplit
                            ? 'Wpisz koszty operacyjne bilansowania za miesiąc. Stawka za MWh to koszty podzielone przez wolumen wszystkich PPE, '
                              + 'więc suma faktur równa się kosztom. Faktury za bilansowanie wychodzą po wpisaniu kosztów, w tym samym rozliczeniu co faktury za energię.'
                            : 'Faktury za bilansowanie powstają automatycznie razem z fakturami za energię. '
                              + 'Jeśli rozliczenie miesiąca przeszło bez stawki, wystaw je tutaj po jej wpisaniu.'}
                    </p>

                    {costSplit && (
                        <form onSubmit={handleAddCost} className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end mb-6 p-4 bg-gray-50 rounded-lg">
                            <div>
                                <label className="block text-gray-700 text-sm mb-1">Rok</label>
                                <select
                                    value={newCost.year}
                                    onChange={(e) => setNewCost({ ...newCost, year: Number(e.target.value) })}
                                    className="p-2 border border-gray-300 rounded-lg"
                                >
                                    {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm mb-1">Miesiąc</label>
                                <select
                                    value={newCost.month}
                                    onChange={(e) => setNewCost({ ...newCost, month: Number(e.target.value) })}
                                    className="p-2 border border-gray-300 rounded-lg"
                                >
                                    {MONTH_NAMES.map((name, index) => <option key={index + 1} value={index + 1}>{name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm mb-1">Koszty operacyjne (zł)</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={newCost.amount}
                                    onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                                    placeholder="np. 1780,41"
                                    className="p-2 border border-gray-300 rounded-lg w-full sm:w-40 text-right"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={savingCost !== null}
                                className="bg-softTeal text-white font-semibold px-6 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center"
                            >
                                <FaPlus className="mr-2" />
                                Zapisz koszty
                            </button>
                        </form>
                    )}

                    {rows.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">
                            {costSplit ? 'Brak rozliczeń i wpisanych kosztów' : 'Brak rozliczeń'}
                        </p>
                    ) : (
                        <>
                            {renderMobileList()}
                            {renderDesktopTable()}
                        </>
                    )}

                    {costSplit && (
                        <p className="text-gray-500 text-xs mt-4">
                            Koszty za miesiąc z wystawionymi fakturami zmienisz dopiero po usunięciu rozliczenia tego miesiąca
                            (Generowanie faktur) — i tylko gdy żadna z jego faktur nie trafiła jeszcze do KSeF.
                        </p>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Balancing;
